<template>
  <NtAlert
    v-if="
      hub.country_filters_enabled &&
      $route.matched[depth].meta.country_visibility.length > 0 &&
      !$route.matched[depth].meta.country_visibility.includes(selectedOffice.country)
    "
    level="danger"
    heading="Access Denied">
    {{ $filters.trans('property.country', selectedOffice.country) }} does not have access to this page.
  </NtAlert>

  <template v-else>
    <NtPageHeader
      v-if="depth === 0"
      :heading="$route.matched[depth].meta.name"
      :introduction="$route.matched[depth].meta.introduction">
      <template v-if="$can('access members hub admin')" #actions>
        <NtButton
          v-if="$route.matched.length === 1"
          title="Edit Page"
          icon="fa-pencil-alt"
          :to="{ name: 'admin.pages.edit', params: { membersHubPage: $route.matched[depth].meta.id } }" />
        <template v-else-if="$route.matched.length > 1">
          <NtButton title="Edit Page" icon="fa-pencil-alt" @click="$refs.editPageModal.open()" />

          <NtModal ref="editPageModal" heading="Select Page">
            <NtSpacing>
              <p>Select which page you would like to edit.</p>

              <div class="table-styled">
                <table>
                  <tbody>
                    <tr v-for="editPage in $route.matched" :key="editPage.id">
                      <td>{{ editPage.meta.name }}</td>
                      <td class="text-right">
                        <NtButton
                          icon="fa-pencil-alt"
                          level="primary"
                          :to="{ name: 'admin.pages.edit', params: { membersHubPage: editPage.meta.id } }">
                          Edit
                        </NtButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </NtSpacing>
          </NtModal>
        </template>
      </template>
    </NtPageHeader>

    <NtLoader :loading="!page" debounce>
      <NtAlert v-if="!page.is_published" class="mb-6" heading="Unpublished Page" level="warning" dismissable>
        Agents cannot see this page while it is unpublished.
      </NtAlert>
      <NtSidebarLayout v-if="page.modules.length > 0">
        <template v-for="m in pageModules" :key="m.id">
          <NtAlert v-if="m.module.permission && $cannot(m.module.permission)" level="danger" heading="Access Denied">
            {{ selectedOffice.name }} does not have permission to {{ m.module.permission }}.
          </NtAlert>
          <component v-else :is="m.module.component" :module-id="m.id" :config="m.config">
            <NtAlert level="danger" heading="Missing Module">
              Module "{{ m.module.component }}" not installed.
              <template #actions>
                <NtButton level="danger" icon="fa-sync" @click="reload"> Reload Hub </NtButton>
              </template>
            </NtAlert>
          </component>
        </template>

        <template v-if="sidebarModules.length > 0" v-slot:sidebar>
          <template v-for="m in sidebarModules" :key="m.id">
            <NtAlert v-if="m.module.permission && $cannot(m.module.permission)" level="danger" heading="Access Denied">
              {{ selectedOffice.name }} does not have permission to {{ m.module.permission }}.
            </NtAlert>
            <component v-else :is="m.module.component" :module-id="m.id" :config="m.config">
              <NtAlert level="danger" heading="Missing Module">
                Module "{{ m.module.component }}" not installed.
                <template #actions>
                  <NtButton level="danger" icon="fa-sync" @click="reload"> Reload Hub </NtButton>
                </template>
              </NtAlert>
            </component>
          </template>
        </template>
      </NtSidebarLayout>

      <div v-if="page.show_tabs" :class="{ 'mt-6': page.modules.length > 0 }">
        <NtTabContainer :tabs="tabs" :selected="selectedTab" />
        <RouterView :depth="depth + 1" />
      </div>
    </NtLoader>
  </template>
</template>

<script>
import { mapActions, mapState } from 'pinia'
import { useSystemStore } from '@/stores/system'
import NtSidebarLayout from '@/layouts/NtSidebarLayout.vue'
import NtPageHeader from '@/components/general/NtPageHeader.vue'
import NtLoader from '@/components/general/NtLoader.vue'
import NtTabContainer from '@/components/navigation/NtTabContainer.vue'
import { RouterView } from 'vue-router'
import NtAlert from '@/components/general/NtAlert.vue'
import { useAuthStore } from '@/stores/auth'
import NtButton from '@/components/general/NtButton.vue'
import NtModal from '@/components/general/NtModal.vue'
import NtSpacing from '@/components/general/NtSpacing.vue'
import { computed } from 'vue'

export default {
  name: 'PageView',
  components: {
    NtSidebarLayout,
    NtPageHeader,
    NtLoader,
    NtTabContainer,
    RouterView,
    NtAlert,
    NtButton,
    NtModal,
    NtSpacing
  },
  props: {
    depth: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      page: null
    }
  },
  provide() {
    return {
      page: computed(() => this.page)
    }
  },
  computed: {
    ...mapState(useSystemStore, ['hub', 'routes', 'confirmExitMethods']),
    ...mapState(useAuthStore, ['selectedOffice']),
    pageModules() {
      return this.page?.modules.filter((m) => !m.sidebar) || []
    },
    sidebarModules() {
      return this.page?.modules.filter((m) => m.sidebar) || []
    },
    selectedTab() {
      return this.page?.show_tabs && this.$route.matched.length > this.depth + 1
        ? this.$route.matched[this.depth + 1].meta.id
        : null
    },
    tabs() {
      if (!this.page?.show_tabs) {
        return null
      }
      return this.routes.filter(
        (r) => r.parent_id === this.$route.matched[this.depth].meta.id && !r.modules.find((m) => m.model !== null)
      )
    }
  },
  methods: {
    ...mapActions(useSystemStore, ['getCallToActions']),
    async fetchPage() {
      try {
        const response = await this.$api.get(`v3/members-hub-pages/${this.$route.matched[this.depth].meta.id}`)
        this.page = response.data

        if (
          (this.page.parent_id === null && this.page.path === '') ||
          this.$helpers.pageIsCallToAction(this.page.id, false)
        ) {
          this.getCallToActions()
        }

        if (this.page.show_tabs && this.$route.matched.length - 1 === this.depth && this.tabs.length > 0) {
          this.$router.replace({ name: `page${this.tabs[0].id}`, query: this.$route.query })
        }
      } catch (e) {
        if (e.code === 'ERR_CANCELED') {
          console.warn('Failed to fetch page due to abort.')
        }
      }
    },
    reload() {
      window.location.reload()
    }
  },
  watch: {
    $route: {
      handler(newVal, oldVal) {
        if (newVal.matched[this.depth].meta.id === oldVal?.matched[this.depth].meta.id) {
          return
        }
        this.page = null
        this.fetchPage()
      },
      immediate: true
    }
  }
}
</script>
